import * as THREE from 'three';
import { clamp, lerp, smoothstep } from '../core/math';

/**
 * Quarter-view follow camera: fixed yaw, mouse-wheel zoom that moves along a
 * curve (closer = flatter pitch), critically damped follow and a trauma-based
 * screen shake (offset proportional to trauma squared).
 */
export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  /** 0 = closest, 1 = farthest. */
  zoom = 0.62;
  targetZoom = 0.62;
  yaw = 0;
  near = { dist: 11, pitch: 34, lookY: 1.2 };
  far = { dist: 30, pitch: 52, lookY: 0.6 };
  /** 0..1 user setting. */
  shakeScale = 1;
  private trauma = 0;
  private target = new THREE.Vector3();
  private vel = new THREE.Vector3();
  private time = 0;
  /** Temporary override (dialogue close-ups, boss intros). */
  private focus: { pos: THREE.Vector3; zoom: number; weight: number; targetWeight: number } | null = null;

  constructor(fov = 35) {
    this.camera = new THREE.PerspectiveCamera(fov, 16 / 9, 0.5, 600);
  }

  addTrauma(amount: number): void {
    this.trauma = Math.min(1, this.trauma + amount);
  }

  wheel(deltaY: number): void {
    this.targetZoom = clamp(this.targetZoom + Math.sign(deltaY) * 0.08, 0, 1);
  }

  snapTo(p: THREE.Vector3): void {
    this.target.copy(p);
    this.vel.set(0, 0, 0);
  }

  setFocus(pos: THREE.Vector3 | null, zoom = 0.1): void {
    if (pos) {
      if (!this.focus) this.focus = { pos: pos.clone(), zoom, weight: 0, targetWeight: 1 };
      else {
        this.focus.pos.copy(pos);
        this.focus.zoom = zoom;
        this.focus.targetWeight = 1;
      }
    } else if (this.focus) {
      this.focus.targetWeight = 0;
    }
  }

  update(dt: number, follow: THREE.Vector3): void {
    this.time += dt;
    this.zoom = lerp(this.zoom, this.targetZoom, 1 - Math.exp(-10 * dt));

    // Critically damped spring toward the follow target (smoothDamp, ~0.12 s).
    const smooth = 0.12;
    const omega = 2 / smooth;
    const x = omega * dt;
    const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
    const change = this.target.clone().sub(follow);
    const temp = this.vel.clone().addScaledVector(change, omega).multiplyScalar(dt);
    this.vel.sub(temp.clone().multiplyScalar(omega)).multiplyScalar(exp);
    this.target.copy(follow).add(change.add(temp).multiplyScalar(exp));

    let lookAt = this.target;
    let zoom = this.zoom;
    if (this.focus) {
      const f = this.focus;
      f.weight = lerp(f.weight, f.targetWeight, 1 - Math.exp(-4 * dt));
      lookAt = this.target.clone().lerp(f.pos, f.weight);
      zoom = lerp(this.zoom, f.zoom, f.weight);
      if (f.targetWeight === 0 && f.weight < 0.01) this.focus = null;
    }

    const z = smoothstep(0, 1, zoom);
    const dist = lerp(this.near.dist, this.far.dist, z);
    const pitch = THREE.MathUtils.degToRad(lerp(this.near.pitch, this.far.pitch, z));
    const lookY = lerp(this.near.lookY, this.far.lookY, z);
    const cam = this.camera;
    cam.position.set(
      lookAt.x + Math.sin(this.yaw) * Math.cos(pitch) * dist,
      lookAt.y + Math.sin(pitch) * dist,
      lookAt.z + Math.cos(this.yaw) * Math.cos(pitch) * dist,
    );
    cam.lookAt(lookAt.x, lookAt.y + lookY, lookAt.z);

    this.trauma = Math.max(0, this.trauma - 1.5 * dt);
    const s = this.trauma * this.trauma * this.shakeScale;
    if (s > 0) {
      const t = this.time * 28;
      cam.position.x += 0.35 * s * noise1(t + 1.7);
      cam.position.y += 0.25 * s * noise1(t + 9.1);
      cam.rotation.z += 0.021 * s * noise1(t + 4.3);
    }
  }

  /** World-space position the camera is currently following. */
  get followPoint(): THREE.Vector3 {
    return this.target;
  }
}

/** Smooth 1D value noise in [-1, 1]. */
function noise1(x: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const h = (n: number) => {
    const s = Math.sin(n * 127.1) * 43758.5453;
    return (s - Math.floor(s)) * 2 - 1;
  };
  const u = f * f * (3 - 2 * f);
  return h(i) * (1 - u) + h(i + 1) * u;
}
